import { createContext, useContext, useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import Home from '@/pages/Home';
import ValuationForm from '@/pages/ValuationForm';
import IntelligenceResults from '@/pages/IntelligenceResults';
import MaintenancePage from '@/components/MaintenancePage';
import DevBanner from '@/components/DevBanner';
import BottomNav from '@/components/BottomNav';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import NovaWidget from '@/components/NovaWidget';
import InspectorForm from './pages/InspectorForm'
import InspectionReport from './pages/InspectionReport'

// Listings feature shelved — components kept in repo, routes redirect to Home.
// To restore: uncomment these three imports + the original routes below, and
// restore the Listings/Saved tabs in BottomNav.js.
// const Listings        = lazy(() => import('./pages/Listings'));
// const SavedListings   = lazy(() => import('./pages/SavedListings'));
// const ListingDetail   = lazy(() => import('./pages/ListingDetail'));
const BookInspection  = lazy(() => import('./pages/BookInspection'));
const BookingConfirm  = lazy(() => import('./pages/BookingConfirm'));
const VerifyConfirm   = lazy(() => import('./pages/VerifyConfirm'));
// const BrokerDashboard = lazy(() => import('./pages/BrokerDashboard'));  // shelved with listings
const Admin           = lazy(() => import('./pages/Admin'));
const Verify          = lazy(() => import('./pages/Verify'));

export const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

// Routes that must keep working even while REACT_APP_MAINTENANCE is on —
// the report link and the inspector form are sent directly to real people
// (buyers, inspectors) and can't bounce to the maintenance splash. The
// maintenance check below runs before BrowserRouter mounts, so this matches
// against window.location.pathname directly rather than useLocation().
const MAINTENANCE_EXEMPT_PATHS = [
  /^\/report\/[^/]+$/,           // /report/:reportId
  /^\/inspect\/[^/]+\/report$/,  // /inspect/:bookingId/report
];

function isMaintenanceExempt(pathname) {
  return MAINTENANCE_EXEMPT_PATHS.some((re) => re.test(pathname));
}

function PublicLayout() {
  return (
    <>
      <Outlet />
      <BottomNav />
    </>
  );
}

function SessionCleaner() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current === '/results' && location.pathname !== '/results') {
      sessionStorage.removeItem('pq_result');
      sessionStorage.removeItem('pq_formData');
    }
    prevPath.current = location.pathname;
  }, [location.pathname]);

  return null;
}

function ResultsWithKey() {
  const location = useLocation();
  return <IntelligenceResults key={location.key} />;
}

// Inspection reports are a standalone document sent to buyers/sellers —
// the chat bubble has no print handling and floats over report content
// on screen and in print, so skip mounting it there rather than fix that
// globally in the widget itself.
function ConditionalNovaWidget() {
  const location = useLocation();
  if (location.pathname.startsWith('/report/')) return null;
  return <NovaWidget />;
}

function App() {
  const [theme, setTheme] = useState(localStorage.getItem('pq_theme') || 'light');

  useEffect(() => {
    document.body.classList.toggle('light-theme', theme === 'light');
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('pq_theme', next);
    setTheme(next);
  };

  if (process.env.REACT_APP_MAINTENANCE === 'true' && !isMaintenanceExempt(window.location.pathname)) {
    return (
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <MaintenancePage />
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <ErrorBoundary>
        <div className={`App${theme === 'light' ? ' light-theme' : ''}`}>
          <BrowserRouter>
            <Suspense fallback={null}>
              <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/"                   element={<Home />} />
                  <Route path="/valuate"             element={<ValuationForm />} />
                  <Route path="/results" element={<ResultsWithKey />} />
                  {/* Listings feature shelved — redirect to Home (reversible). Original routes below. */}
                  <Route path="/listings"            element={<Navigate to="/" replace />} />
                  <Route path="/saved"               element={<Navigate to="/" replace />} />
                  <Route path="/listings/:id"        element={<Navigate to="/" replace />} />
                  {/* <Route path="/listings"            element={<Listings />} /> */}
                  {/* <Route path="/saved"               element={<SavedListings />} /> */}
                  {/* <Route path="/listings/:id"        element={<ListingDetail />} /> */}
                  <Route path="/inspect"             element={<BookInspection />} />
                  <Route path="/inspect/:id"         element={<BookInspection />} />
                  <Route path="/inspect/:id/confirm" element={<BookingConfirm />} />
                  <Route path="/verify"              element={<Verify />} />
                  <Route path="/verify/confirm"      element={<VerifyConfirm />} />
                </Route>
                {/* Listings feature shelved — redirect to Home (reversible). */}
                <Route path="/broker" element={<Navigate to="/" replace />} />
                {/* <Route path="/broker" element={<BrokerDashboard />} /> */}
                <Route path="/admin"  element={<Admin />} />
                <Route path="/inspect/:bookingId/report" element={<InspectorForm />} />
                <Route path="/report/:reportId" element={<InspectionReport />} />
              </Routes>
            </Suspense>
            <SessionCleaner />
            <ConditionalNovaWidget />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </ErrorBoundary>
      <DevBanner />
    </ThemeContext.Provider>
  );
}

export default App;
